import type { StatusTone } from '@beonit/adaptive-raindrop';
import { isRunFinished, type AnalysisRun, type RunSourceType, type RunStatus } from '../model.js';
import { engineShortName } from './engine-names.js';

/**
 * How a run reads on screen: its state, its age, what can be done to it.
 *
 * Ported from the legacy frontend's `queueFormat.js`, which is pure and has its own
 * tests there. Three things changed on the way over, and all three are the point of
 * porting rather than copying:
 *
 * - **Labels are in Spanish.** The legacy tool is in English; this back-office is not.
 * - **Colours are design system tones**, not Vuetify's `'grey darken-2'`.
 * - **`describeActionError` did not come.** It read the HTTP status and the server's
 *   English text to guess a message. This repository already decides the message from
 *   the error *code*, in `feedback.ts`, which is both shorter and right when Virtual
 *   Profiler answers 409 for six unrelated reasons.
 *
 * Everything here is a function of a run and a clock. `now` is a parameter and never
 * `Date.now()` inside a branch: a helper that reads the clock cannot be tested, only
 * hoped about.
 */

/**
 * Defaults for the two server windows, until the read model publishes them.
 *
 * They mirror Virtual Profiler's `VP_HEARTBEAT_TTL_S` and `VP_CANCEL_GRACE_S`. If the
 * server is configured differently, the screen is early or late in offering the
 * «mark as failed» button and nothing worse — which is why guessing is acceptable
 * here and would not be for anything that writes.
 */
export const DEFAULT_HEARTBEAT_TTL_S = 600;
export const DEFAULT_CANCEL_GRACE_S = 180;

export interface StatusPresentation {
  label: string;
  tone: StatusTone;
  /** Whether the state is one the run is still moving through. */
  spinner: boolean;
}

const STATUS_PRESENTATION: Readonly<Record<RunStatus, StatusPresentation>> = Object.freeze({
  QUEUED: { label: 'En cola', tone: 'neutral', spinner: false },
  DISPATCHED: { label: 'Enviada', tone: 'info', spinner: true },
  RUNNING: { label: 'Analizando', tone: 'info', spinner: true },
  /**
   * The state interfaces forget exists. Cancellation is cooperative, so a run sits
   * here until it reaches its next checkpoint — and «cancelada» would be a lie while
   * it is still running.
   */
  CANCEL_REQUESTED: { label: 'Cancelación pedida', tone: 'warning', spinner: true },
  /** Its execution was paused before it started: it waits for somebody, not for a slot. */
  PAUSED: { label: 'En pausa', tone: 'neutral', spinner: false },
  SUCCEEDED: { label: 'Completada', tone: 'success', spinner: false },
  FAILED: { label: 'Fallida', tone: 'danger', spinner: false },
  CANCELLED: { label: 'Cancelada', tone: 'neutral', spinner: false },
});

/**
 * A state Virtual Profiler invented shows its own name rather than nothing.
 *
 * The contract test would have caught it in CI, but that does not help the operator
 * looking at the screen today: an empty badge says the run has no state, and every
 * state it can be in matters to what they do next.
 */
export function statusPresentation(status: RunStatus | string): StatusPresentation {
  return (
    STATUS_PRESENTATION[status as RunStatus] ?? {
      label: status ? String(status) : 'Desconocido',
      tone: 'neutral',
      spinner: false,
    }
  );
}

interface SourcePresentation {
  label: string;
  /** Route name in this back-office, or `null` when there is no screen to open. */
  routeName: string | null;
}

/**
 * Where a run came from.
 *
 * The routes are this back-office's, not the legacy tool's. `cv` has no screen here
 * and honestly says so: a link to nowhere is worse than no link.
 */
const SOURCE_PRESENTATION: Readonly<Record<RunSourceType, SourcePresentation>> = Object.freeze({
  interview: { label: 'Entrevista', routeName: null },
  manual_analysis: { label: 'Vídeo', routeName: 'virtual-profiler-video-detail' },
  cv: { label: 'CV', routeName: null },
});

export function sourcePresentation(sourceType: RunSourceType | string): SourcePresentation {
  return SOURCE_PRESENTATION[sourceType as RunSourceType] ?? { label: 'Origen', routeName: null };
}

/** What the run is about, in words: the video's name, or «Vídeo 101» when it has none. */
export function sourceLabel(
  run: Pick<AnalysisRun, 'sourceType' | 'sourceId'> & Partial<Pick<AnalysisRun, 'sourceName'>>,
): string {
  // The operator's name for it first: «Vídeo 307» is how upstream keys it, not how anybody
  // knows it.
  const name = run.sourceName?.trim();
  if (name) return name;

  const { label } = sourcePresentation(run.sourceType);

  return run.sourceId ? `${label} ${run.sourceId}` : label;
}

/** Seconds since an instant, never negative. `null` when there is no instant. */
export function ageSeconds(value: Date | null | undefined, now: number): number | null {
  if (!value) return null;

  const ms = value.getTime();
  if (!Number.isFinite(ms)) return null;

  // A clock slightly behind the server's would otherwise produce «hace -3 s».
  return Math.max(0, Math.round((now - ms) / 1000));
}

/** Compact relative time: «ahora mismo», «hace 45 s», «hace 3 min», «hace 2 h», «hace 1 d». */
export function formatAge(value: Date | null | undefined, now: number): string {
  const seconds = ageSeconds(value, now);
  if (seconds === null) return '';

  if (seconds < 5) return 'ahora mismo';
  if (seconds < 60) return `hace ${seconds} s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;

  return `hace ${Math.floor(hours / 24)} d`;
}

/** How long something took: «48 s», «4 m 12 s», «1 h 03 m». */
export function formatDuration(from: Date | null | undefined, to: Date | number): string {
  if (!from) return '';

  const end = to instanceof Date ? to.getTime() : to;
  const total = Math.max(0, Math.round((end - from.getTime()) / 1000));

  if (total < 60) return `${total} s`;

  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes} m ${String(total % 60).padStart(2, '0')} s`;

  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} m`;
}

/** Compact size for the health strip: «1.4 GB», «130 MB», «900 kB». */
export function formatBytes(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  if (value < 1000) return `${Math.round(value)} B`;

  const units = ['kB', 'MB', 'GB', 'TB'];
  let size = value / 1000;
  let unit = 0;

  while (size >= 1000 && unit < units.length - 1) {
    size /= 1000;
    unit += 1;
  }

  // One decimal below ten, none above: «1.4 GB» is informative, «847.3 MB» is noise.
  return `${size >= 10 ? Math.round(size) : size.toFixed(1)} ${units[unit]}`;
}

/**
 * Last sign of life from the worker.
 *
 * Falls back through start and dispatch, because a run that has just been picked up
 * has no heartbeat yet and would otherwise look lost the instant it started.
 */
export function lastSeenAt(run: AnalysisRun): Date | null {
  return run.heartbeatAt ?? run.startedAt ?? null;
}

export function isHeartbeatStale(
  run: AnalysisRun,
  now: number,
  ttlSeconds: number = DEFAULT_HEARTBEAT_TTL_S,
): boolean {
  const seconds = ageSeconds(lastSeenAt(run), now);

  return seconds !== null && seconds > ttlSeconds;
}

/** Whether a cancellation has been pending long enough to offer the forced one. */
export function cancelGraceElapsed(
  run: AnalysisRun,
  now: number,
  graceSeconds: number = DEFAULT_CANCEL_GRACE_S,
): boolean {
  const seconds = ageSeconds(run.cancellation?.requestedAt ?? null, now);

  return seconds !== null && seconds >= graceSeconds;
}

export interface EngineChip {
  key: string;
  text: string;
  /** A build hash reads as a hash; a version reads as text. */
  mono: boolean;
  tooltip?: string;
  /** A build stamped `dev` never came from a release, and the screen has to say so. */
  dev?: boolean;
}

const HIGHLIGHTED_ENGINES = ['gestures', 'coherence'] as const;

/**
 * Chips naming the engines a run used.
 *
 * Empty until the worker stamps them, which is correct rather than unfortunate: a
 * queued run has no engine versions, and inventing «pendiente» would put a word
 * where a fact belongs.
 */
export function engineChips(run: AnalysisRun): EngineChip[] {
  const block = run.engine;
  if (!block) return [];

  const engines = block.engines ?? {};
  const chips: EngineChip[] = HIGHLIGHTED_ENGINES.filter((name) => engines[name] != null).map(
    (name) => ({ key: name, text: `${engineShortName(name)} ${engines[name]}`, mono: false }),
  );

  // The rest are collapsed into a count: the two above are what anybody compares,
  // and a row of eight chips stops being readable.
  const others = Object.keys(engines).filter(
    (name) => !HIGHLIGHTED_ENGINES.includes(name as (typeof HIGHLIGHTED_ENGINES)[number]),
  );

  if (others.length > 0) {
    chips.push({
      key: 'others',
      text: `+${others.length} motores`,
      mono: false,
      tooltip: others.map((name) => `${engineShortName(name)} ${engines[name]}`).join(' · '),
    });
  }

  if (block.buildSha) {
    chips.push({
      key: 'build',
      text: `build ${block.buildSha.slice(0, 7)}`,
      mono: true,
      dev: block.buildSha === 'dev',
    });
  }

  return chips;
}

export interface AvailableActions {
  /** Take it out of the queue. Only before it starts, and never called «borrar». */
  withdraw: boolean;
  cancel: boolean;
  force: boolean;
  markFailed: boolean;
  forceGraceElapsed: boolean;
  heartbeatStale: boolean;
}

export interface ActionContext {
  now: number;
  heartbeatTtlS?: number;
  cancelGraceS?: number;
  /** Forced cancellation is a server flag; the button hides when it is off. */
  forceEnabled?: boolean;
}

/**
 * What can be done to a run right now.
 *
 * The two that must never be confused are the first two. `withdraw` takes a run out
 * of the queue before it starts and erases nothing; `cancel` stops one that is
 * running. They are never both true, and offering the wrong one is the likeliest
 * mistake on this screen.
 */
export function availableActions(run: AnalysisRun, context: ActionContext): AvailableActions {
  const { now } = context;
  const running = run.status === 'RUNNING' || run.status === 'CANCEL_REQUESTED';
  const heartbeatStale =
    running && isHeartbeatStale(run, now, context.heartbeatTtlS ?? DEFAULT_HEARTBEAT_TTL_S);

  return {
    withdraw: run.status === 'QUEUED' || run.status === 'DISPATCHED',
    cancel: run.status === 'RUNNING' && !isRunFinished(run.status),
    force:
      run.status === 'CANCEL_REQUESTED' &&
      context.forceEnabled !== false &&
      run.cancellation?.forced !== true,
    // Only a run nobody has heard from. Marking a live run as failed would lose a
    // result that was on its way.
    markFailed: heartbeatStale,
    forceGraceElapsed: cancelGraceElapsed(run, now, context.cancelGraceS ?? DEFAULT_CANCEL_GRACE_S),
    heartbeatStale,
  };
}
